<script setup>
import { useReferenceWorkspace } from "./referenceWorkspace";
import QuoteAddressManager from "@/components/QuoteAddressManager.vue";
import ReferenceSummary from "@/components/ReferenceSummary.vue";
import ReferenceOperationalSummary from "./ReferenceOperationalSummary.vue";
import * as Icon from "@heroicons/vue/24/solid";
const {
  reference,
  sectionSaving,
  sectionEditing,
  sectionEditButtonClasses,
  sectionDoneButtonClasses,
  setSectionRef,
  setEditingSection,
  stopEditing,
  saveSection,
  referenceLocations,
  referenceLocationAddresses,
  addLocationToReferenceLocations,
} = useReferenceWorkspace();
</script>

<template>
  <div
    class="reference-overview-card border border-slate-200 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 2xl:col-span-12"
  >
    <div class="reference-overview-layout">
      <section
        id="overview"
        :ref="(el) => setSectionRef('overview', el)"
        class="reference-overview-panel min-w-0 scroll-mt-24"
      >
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div
              class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
            >
              Overview
            </div>
            <h2
              class="text-xl font-semibold text-slate-900 dark:text-slate-100"
            >
              Job overview
            </h2>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-if="!sectionEditing.overview"
              :class="sectionEditButtonClasses"
              @click.prevent="setEditingSection('overview')"
            >
              <Icon.PencilSquareIcon class="mr-1 inline h-4 w-4" /> Edit
            </button>
            <button
              v-if="sectionEditing.overview"
              :class="sectionDoneButtonClasses"
              @click.prevent="stopEditing('overview')"
            >
              Done
            </button>
            <button
              v-if="sectionEditing.overview"
              class="btn save"
              :disabled="sectionSaving.overview"
              @click.prevent="saveSection('overview')"
            >
              <Icon.CheckIcon
                v-if="!sectionSaving.overview"
                class="mr-1 inline h-4 w-4"
              />{{ sectionSaving.overview ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>

        <div class="space-y-5">
          <div
            class="break-words text-lg font-semibold text-slate-900 dark:text-slate-100"
          >
            <TextField
              :data="reference.data.name"
              :editable="sectionEditing.overview"
              @update="(val) => (reference.data.name = val)"
            />
          </div>
          <ReferenceSummary
            embedded
            :reference="reference.data"
            :editable="sectionEditing.overview"
            @changeCustomer="(customer) => reference.setCustomer(customer)"
          />
          <ReferenceOperationalSummary
            :reference="reference.data"
            :version="reference.resources"
          />
        </div>
      </section>

      <section
        id="locations"
        :ref="(el) => setSectionRef('locations', el)"
        class="reference-locations-panel min-w-0 scroll-mt-24 border-slate-200 dark:border-slate-700"
      >
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div
              class="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400"
            >
              Locations
            </div>
            <h2
              class="text-xl font-semibold text-slate-900 dark:text-slate-100"
            >
              Site Addresses
            </h2>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <button
              v-if="!sectionEditing.locations"
              :class="sectionEditButtonClasses"
              @click.prevent="setEditingSection('locations')"
            >
              <Icon.PencilSquareIcon class="mr-1 inline h-4 w-4" /> Edit
            </button>
            <button
              v-if="sectionEditing.locations"
              :class="sectionDoneButtonClasses"
              @click.prevent="stopEditing('locations')"
            >
              Done
            </button>
            <button
              v-if="sectionEditing.locations"
              class="btn save"
              :disabled="sectionSaving.locations"
              @click.prevent="saveSection('locations')"
            >
              <Icon.CheckIcon
                v-if="!sectionSaving.locations"
                class="mr-1 inline h-4 w-4"
              />{{ sectionSaving.locations ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>
        <div>
          <p
            v-if="sectionEditing.locations"
            class="mb-4 text-sm text-slate-500 dark:text-slate-400"
          >
            Add site addresses here, then attach them to shifts in Schedule
            management.
          </p>
          <QuoteAddressManager
            layout="overview"
            v-if="referenceLocationAddresses.length || sectionEditing.locations"
            :addresses="referenceLocationAddresses"
            :editable="sectionEditing.locations"
            :defaultCenter="{ lat: 54.5, lng: -3.2 }"
            :defaultZoom="6"
            :singlePointZoom="6"
            :multiplePointZoom="6"
            emit-address-objects
            @add:address="addLocationToReferenceLocations"
          />
          <div v-else class="space-y-2">
            <div v-if="referenceLocations.length" class="space-y-2">
              <div
                v-for="location in referenceLocations"
                :key="location.id"
                class="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                {{ location.label }}
              </div>
            </div>
            <div
              v-else-if="!referenceLocations.length"
              class="rounded-xl border border-dashed border-slate-300 px-4 py-6 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-300"
            >
              No locations assigned yet.
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.reference-overview-card {
  container-type: inline-size;
  min-width: 0;
  border-radius: 1.25rem;
  box-shadow: 0 8px 24px -20px rgb(15 23 42 / 0.3);
}
.reference-overview-layout {
  display: grid;
  min-width: 0;
}
.reference-overview-panel,
.reference-locations-panel {
  padding: clamp(1rem, 2.5vw, 1.75rem);
}
.reference-locations-panel {
  border-top-width: 1px;
}
@container (min-width: 56rem) {
  .reference-overview-layout {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr);
  }
  .reference-locations-panel {
    border-top-width: 0;
    border-left-width: 1px;
  }
}
</style>
